import { type Event, isEvent } from '../event';
import { type FrameStart, isFrameStart } from '../../metadata/frameStart';
import type { AnyEventBase } from '@snapchat/blizzard-schema';
import { getLogger } from '../../logger';
import { transmitJsonEvents } from '../../network/transmit';
import type { Logger } from 'loglevel';

/**
 * A log queue, which receives loggable items and gets them ready to be transmitted. Each log queue
 * maintains an internal sequence ID counter which is attached to events to detect gaps.
 *
 * @internal
 */
export interface LogQueue<T> {
  append(item: Event<T> | FrameStart): Promise<void>;
}

/**
 * A simple single log queue which supports standard schema events and transmits them using JSON. It
 * doesn't currently buffer events (in memory or disk) for batching/error handling.
 *
 * In the future, we may have a separate log queue for tier-0 events which has a higher upload
 * priority.
 *
 * @internal
 */
export class JsonLogQueue implements LogQueue<AnyEventBase> {
  private readonly logQueueName: string;
  private lastFrameStart: FrameStart | undefined;
  private sequenceId = 1;
  private log: Logger;

  constructor(logQueueName: string) {
    this.logQueueName = logQueueName;
    this.log = getLogger(`jsonQueue.${logQueueName}`);
  }

  /** @override */
  async append<T extends AnyEventBase>(item: Event<T> | FrameStart): Promise<void> {
    // Our downstream transmitter only supports Blizzard schema JSON today.
    // We need to flatten the metadata onto the items and merge them with the last FrameStart we saw.
    // When we migrate the backend to supporting Frames, we can instead create appropriately sized batches here
    // and transmit them in a Frame with a single FrameStart.

    if (isFrameStart(item)) {
      this.log.trace('Saw new FrameStart, copying for this queue');
      this.lastFrameStart = {
        ...item,
        log_queue_name: this.logQueueName,
      };
    } else if (isEvent<T>(item)) {
      if (this.lastFrameStart === undefined) {
        throw new Error('Event before FrameStart'); // Impossible state
      }

      const event: T = {
        ...item.data,
        ...item.metadata,
        ...this.lastFrameStart,

        log_queue_sequence_id: this.sequenceId++,
      };

      await transmitJsonEvents([event]);
    } else {
      throw new Error(`Illegal item: ${item}`); // Impossible state
    }
  }
}

/**
 * The single default log queue for Blizzard
 *
 * @internal
 */
export const jsonLogQueue = new JsonLogQueue('w');
